/**
 * @fileoverview Manejador centralizado de errores HTTP
 * Transforma errores de axios en instancias de ApiError estructuradas
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

import router from '@/app/router';
import { useAuthStore } from '@/modules/auth/stores/auth.store';
import type { AxiosError } from 'axios';
import { ApiError, type ApiErrorResponse } from '../types';
import { detectNetworkErrorType, getNetworkErrorMessage } from './networkDetector';

/**
 * Verifica si un error de axios tiene una respuesta estructurada del API
 * @param error - Error de axios
 * @returns true si el error tiene estructura de ApiErrorResponse
 */
const isApiErrorResponse = (error: AxiosError): error is AxiosError<ApiErrorResponse> => {
  return (
    error.response !== undefined &&
    error.response.data !== null &&
    error.response.data !== undefined &&
    typeof error.response.data === 'object' &&
    'dataError' in error.response.data &&
    'success' in error.response.data &&
    'showErrorToClient' in error.response.data
  );
};

/**
 * Maneja errores 401 (No autorizado)
 * Ejecuta logout automático y redirige a login
 */
const handle401Error = async (): Promise<void> => {
  const authStore = useAuthStore();

  // Limpiar sesión
  await authStore.destroyLoginInfo();

  // Redirigir a login
  await router.replace({ name: 'login' });
};

/**
 * Maneja errores 403 (Prohibido)
 * Redirige a página de acceso denegado
 */
const handle403Error = async (): Promise<void> => {
  await router.replace({ name: 'forbidden' });
};

/**
 * Maneja errores 404 (No encontrado)
 * Redirige a página 404
 */
const handle404Error = async (): Promise<void> => {
  await router.replace({ name: 'notFound' });
};

/**
 * Transforma un error de axios en una instancia de ApiError
 *
 * @param error - Error de axios a transformar
 * @returns Instancia de ApiError con información estructurada
 *
 * @example
 * try {
 *   await apiMunzen.get('/endpoint');
 * } catch (error) {
 *   const apiError = handleApiError(error);
 *   console.log(apiError.dataError);
 *   console.log(apiError.status);
 * }
 */
export const handleApiError = (error: unknown): ApiError => {
  // Verificar si es un error de axios
  if (!error || typeof error !== 'object' || !('isAxiosError' in error)) {
    // Error genérico no relacionado con axios
    return new ApiError(
      {
        success: false,
        showErrorToClient: true,
        dataError: 'Error inesperado. Por favor, intenta nuevamente.',
        errorTrackingUUID: null,
        persistentErrorMessage: false,
        title: null,
      },
      500,
    );
  }

  const axiosError = error as AxiosError;

  // Error de red (sin respuesta del servidor)
  if (!axiosError.response) {
    // Detectar tipo específico de error de red
    const errorType = detectNetworkErrorType(axiosError);
    const message = getNetworkErrorMessage(axiosError);

    // Título según tipo de error
    const title =
      errorType === 'offline'
        ? 'Sin Conexión'
        : errorType === 'timeout'
          ? 'Tiempo Agotado'
          : 'Error de Conexión';

    return new ApiError(
      {
        success: false,
        showErrorToClient: true,
        dataError: message,
        errorTrackingUUID: null,
        persistentErrorMessage: true, // Errores de red son persistentes
        title,
      },
      0,
    );
  }

  const status = axiosError.response.status;

  // Manejar errores específicos por código de estado
  if (status === 401) {
    handle401Error().catch(console.error);
  } else if (status === 403) {
    handle403Error().catch(console.error);
  } else if (status === 404) {
    handle404Error().catch(console.error);
  }

  // Si el error tiene estructura de ApiErrorResponse, usarla
  if (isApiErrorResponse(axiosError)) {
    return new ApiError(axiosError.response.data, status);
  }

  // Error del servidor sin estructura esperada
  const defaultMessages: Record<number, { title: string; message: string }> = {
    400: {
      title: 'Solicitud Inválida',
      message: 'Solicitud inválida. Verifica los datos enviados.',
    },
    401: {
      title: 'Sesión Expirada',
      message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
    },
    403: {
      title: 'Acceso Denegado',
      message: 'No tienes permisos para realizar esta acción.',
    },
    404: {
      title: 'No Encontrado',
      message: 'El recurso solicitado no fue encontrado.',
    },
    500: {
      title: 'Error del Servidor',
      message: 'Error interno del servidor. Por favor, intenta más tarde.',
    },
    502: {
      title: 'Servicio No Disponible',
      message: 'Servicio temporalmente no disponible. Intenta más tarde.',
    },
    503: {
      title: 'Servicio en Mantenimiento',
      message: 'Servicio en mantenimiento. Intenta más tarde.',
    },
  };

  const errorInfo = defaultMessages[status] || {
    title: 'Error del Servidor',
    message: `Error del servidor (${status}). Por favor, intenta nuevamente.`,
  };

  return new ApiError(
    {
      success: false,
      showErrorToClient: true,
      dataError: errorInfo.message,
      errorTrackingUUID: null,
      persistentErrorMessage: status >= 500,
      title: errorInfo.title,
    },
    status,
  );
};

/**
 * Formatea errores de validación (dataError como objeto) a string legible
 * @param dataError - Errores de validación por campo
 * @returns String con todos los errores formateados
 *
 * @example
 * const errors = {
 *   email: ['El campo email es obligatorio'],
 *   password: ['La contraseña debe tener al menos 8 caracteres']
 * };
 * formatValidationErrors(errors);
 * // Retorna: "email: El campo email es obligatorio\npassword: La contraseña debe tener al menos 8 caracteres"
 */
export const formatValidationErrors = (dataError: Record<string, string[]>): string => {
  return Object.entries(dataError)
    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
    .join('\n');
};

/**
 * Obtiene el mensaje de error como string, formateando si es necesario
 * @param error - Error del API
 * @returns Mensaje de error como string
 *
 * @example
 * const error = new ApiError({ dataError: { email: ['Campo requerido'] }, ... }, 422);
 * getErrorMessage(error); // "email: Campo requerido"
 */
export const getErrorMessage = (error: ApiError): string => {
  if (typeof error.dataError === 'string') {
    return error.dataError;
  }
  return formatValidationErrors(error.dataError);
};

/**
 * Verifica si un error debe mostrarse al usuario
 * @param error - Error a verificar
 * @returns true si el error debe mostrarse al usuario
 */
export const shouldShowErrorToUser = (error: ApiError): boolean => {
  return error.showErrorToClient;
};

/**
 * Verifica si un mensaje de error debe persistir en pantalla
 * @param error - Error a verificar
 * @returns true si el mensaje debe persistir
 */
export const shouldPersistError = (error: ApiError): boolean => {
  return error.persistentErrorMessage;
};

/**
 * Formatea mensajes de error HTML para mostrarlos en toasts
 * - Reemplaza tags <strong> con clase CSS 'bold-font'
 * - Reemplaza 'baseURL' con el origen de la ventana actual
 *
 * @param message - Mensaje HTML a formatear
 * @returns Mensaje formateado para toasts
 *
 * @example
 * formatToastMessage('Error con <strong>UUID</strong> en baseURL');
 * // Retorna: 'Error con <span class="bold-font">UUID</span> en http://localhost:5173'
 */
export const formatToastMessage = (message: string): string => {
  let formatted = message;

  // Reemplazar strong tags con clase CSS
  formatted = formatted.replace(/<strong>(.*?)<\/strong>/g, '<span class="bold-font">$1</span>');

  // Reemplazar baseURL con origen actual
  formatted = formatted.replace(/baseURL/g, window.location.origin);

  return formatted;
};
